import {
  Box,
  CancelIconButton,
  CrudMode,
  DataArray,
  DataDisplay,
  GeneralValidationErrors,
  Grid,
  Stack,
  Typography,
  ValidationErrors,
  formatMinutes,
  getSpacing,
  pn,
  useElementBreakpoints,
} from "@compass/core";
import { DurationInput, Input, Select } from "components";
import {
  Permission,
  StudentServiceTemplate,
  StudentServiceTemplateAdditionalEntry,
  StudentServiceTemplateEditResource,
  StudentServiceTemplateViewResource,
} from "generated/openApi";
import EscalatorWarningIcon from "@mui/icons-material/EscalatorWarning";
import produce from "immer";
import "moment-timezone";

export const AdditionalEntry = (props: {
  rootPermission: Permission;
  crudMode: CrudMode;
  entry: StudentServiceTemplateAdditionalEntry;
  setEntry: (value: StudentServiceTemplateAdditionalEntry) => void;
  setEditMode: () => void;
  viewResource: StudentServiceTemplateViewResource;
  editResource: StudentServiceTemplateEditResource;
  validationErrors: ValidationErrors | string[];
  cancelCreate: () => void;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, largeFieldBreakpoints } = useElementBreakpoints();
  const properties = props.viewResource.properties || [];
  const generationValidationErrors = props.validationErrors[pn<StudentServiceTemplate>("generalValidationErrors")];
  const selectedServiceType = props.viewResource.serviceTypes?.find((x) => x.id === props.entry.serviceTypeId);
  const serviceSubTypes = props.editResource.serviceSubTypes?.filter(
    (x) => x.serviceTypeId === props.entry.serviceTypeId
  );

  const serviceTypeField = () => {
    const propertyName = pn<StudentServiceTemplate>("serviceTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const value = props.viewResource.serviceTypes?.find(x => x.id == props.entry.serviceTypeId);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <Stack direction="row" spacing="sm" alignItems="center">
          <Typography variant="inherit">{value?.name}</Typography>
          {value?.isForParent && <EscalatorWarningIcon />}
        </Stack>
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.viewResource.serviceTypes?.find((x) => x.id === props.entry.serviceTypeId)}
        options={props.viewResource.serviceTypes}
        groupBy={(option) => (option.isForParent ? "Parent" : "Student")}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEntry(
            produce(props.entry, (draft) => {
              draft.serviceTypeId = value?.id;
              draft.serviceSubTypeId = undefined;
              //draft.parentCount = undefined;
            })
          )
        }
      />
    );
  };

  const serviceSubTypeField = () => {
    const propertyName = pn<StudentServiceTemplate>("serviceSubTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const value = serviceSubTypes?.find(x => x.id == props.entry.serviceSubTypeId);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{value?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={serviceSubTypes?.find((x) => x.id === props.entry.serviceSubTypeId)}
        options={serviceSubTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEntry(
            produce(props.entry, (draft) => {
              draft.serviceSubTypeId = value?.id;
            })
          )
        }
      />
    );
  };

  const parentCountField = () => {
    const propertyName = pn<StudentServiceTemplate>("parentCount");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.entry.parentCount}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        required
        errors={props.validationErrors[propertyName]}
        value={props.entry.parentCount}
        onChange={(e) =>
          props.setEntry(
            produce(props.entry, (draft) => {
              draft.parentCount = parseFloat(e.target.value);
            })
          )
        }
      />
    );
  };

  const durationInMinutesField = () => {
    const propertyName = pn<StudentServiceTemplate>("durationInMinutes");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatMinutes(props.entry.durationInMinutes)}</DataDisplay>
    ) : (
      <DurationInput
        required
        errors={props.validationErrors[propertyName]}
        value={props.entry.durationInMinutes}
        onChange={(value) =>
          props.setEntry(
            produce(props.entry, (draft) => {
              draft.durationInMinutes = value!;
            })
          )
        }
      />
    );
  };

  const collegesField = () => {
    const propertyName = pn<StudentServiceTemplate>("colleges");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <DataArray value={props.entry.colleges?.map((x) => x.name!)} />
      </DataDisplay>
    ) : (
      <Select
        multiple
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.entry.colleges}
        options={props.editResource.colleges}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setEntry(
            produce(props.entry, (draft) => {
              draft.colleges = values;
            })
          )
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const schoolField = () => {
    const propertyName = pn<StudentServiceTemplate>("schoolId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const value = props.editResource.schools?.find(x => x.id == props.entry.schoolId);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{value?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.schools?.find((x) => x.id === props.entry.schoolId)}
        options={props.editResource.schools}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEntry(
            produce(props.entry, (draft) => {
              draft.schoolId = value?.id;
            })
          )
        }
      />
    );
  };

  return (
    <Stack direction="row" alignItems="center" p={getSpacing("md")} sx={{ marginLeft: "-15px" }}>
      <Box flexGrow={1}>
        <Grid ref={breakpointsRef} container spacing="sm">
          <Grid item xs={smallFieldBreakpoints}>
            {serviceTypeField()}
          </Grid>

          {serviceSubTypes && serviceSubTypes.length > 0 && (
            <Grid item xs={smallFieldBreakpoints}>
              {serviceSubTypeField()}
            </Grid>
          )}

          {selectedServiceType?.isForParent && (
            <Grid item xs={smallFieldBreakpoints}>
              {parentCountField()}
            </Grid>
          )}

          <Grid item xs={smallFieldBreakpoints}>
            {durationInMinutesField()}
          </Grid>

          {selectedServiceType?.aprStudentService?.isServiceLocationForCollege ? (
            <Grid item xs={largeFieldBreakpoints}>
              {collegesField()}
            </Grid>
          ) : (
            <Grid item xs={smallFieldBreakpoints}>
              {schoolField()}
            </Grid>
          )}

          {generationValidationErrors && (
            <Grid item xs={12}>
              <GeneralValidationErrors errors={generationValidationErrors} />
            </Grid>
          )}
        </Grid>
      </Box>

      {props.crudMode === "edit" &&<CancelIconButton onClick={props.cancelCreate} />}
    </Stack>
  );
};
